import type { MinMax } from '@iheartjane/types';
import { useCallback } from 'react';

import { isDefined } from '@jane/bloom/utils/guards/isDefined';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import type { FilterGroup } from '@jane/bloom/viewModels/productSearchFilters';

import { useFilterSearchParams } from './useFilterSearchParams';
import {
  filterRangeFromSearchParams,
  searchParamsFromFilterKey,
  searchParamsFromFilterKeyRange,
  singularFilterKey,
} from './utils';

export const useSearchParamFilters = <T extends string = never>(
  ...filterKeys: T[]
) => {
  const [searchParams, setSearchParams] = useFilterSearchParams();
  const searchText = searchParams.get(SEARCH_TERM_QUERY_KEY);

  const clearAllFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    if (searchText) {
      newParams.set(SEARCH_TERM_QUERY_KEY, searchText);
    }
    setSearchParams(newParams);
  }, [searchText, setSearchParams]);

  const clearFilter = useCallback(
    (key: T) =>
      filterKeys.includes(key) &&
      setSearchParams(
        (prev) => {
          prev.delete(singularFilterKey(key));
          return prev;
        },
        { preventScrollReset: true }
      ),
    [filterKeys, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        filterKeys
          .filter((key) => key !== 'searchText')
          .forEach((key) => {
            prev.delete(singularFilterKey(key));
          });
        return prev;
      },
      { preventScrollReset: true }
    );
  }, [filterKeys, setSearchParams]);

  const getSelectedFilterValues = useCallback(
    (): FilterGroup[] =>
      filterKeys
        .map((filterKey) => {
          const filterValues = searchParams.getAll(
            singularFilterKey(filterKey)
          );
          return filterValues.length > 0
            ? { filterKey, filterValues }
            : undefined;
        })
        .filter(isDefined)
        .filter(({ filterKey }) => filterKey !== SEARCH_TERM_QUERY_KEY),
    [filterKeys, searchParams]
  );

  const getFilterRangeValue = useCallback(
    (key: T) =>
      filterKeys.includes(key)
        ? filterRangeFromSearchParams(searchParams, singularFilterKey(key))
        : undefined,
    [filterKeys, searchParams]
  );

  const setFilterRange = useCallback(
    (key: T, minMax: MinMax) => {
      if (!filterKeys.includes(key)) return;

      const updated = searchParamsFromFilterKeyRange(
        searchParams,
        singularFilterKey(key),
        minMax
      );
      setSearchParams(updated, {
        preventScrollReset: true,
      });
    },
    [filterKeys, searchParams, setSearchParams]
  );

  const setFilter = useCallback(
    (key: T, option: string, value: boolean) => {
      const updated = searchParamsFromFilterKey(
        searchParams,
        singularFilterKey(key),
        option,
        value
      );
      setSearchParams(updated, {
        preventScrollReset: true,
      });
    },
    [searchParams, setSearchParams]
  );

  return {
    clearAllFilters,
    clearFilter,
    clearFilters,
    getFilterRangeValue,
    getSelectedFilterValues,
    searchParams,
    setFilter,
    setFilterRange,
    setSearchParams,
  };
};
